import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MENU_ITEMS } from "../menuData";

// Asset imports
import p1 from "../assets/product/1.png";
import p2 from "../assets/product/2.png";
import p4 from "../assets/product/4.png";
import p5 from "../assets/product/5.png";
import friedRice from "../assets/fried_rice_plate.png";
import pizzaPlate from "../assets/pizza_plate.png";
import chickenLeg from "../assets/chicken_leg_plate.png";
import eggSalad from "../assets/egg_salad_plate.png";
import burger from "../assets/promo_chicken_burger.png";
import roll from "../assets/promo_chicken_roll.png";

// Inline Icons
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ClocheIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M2 18h20v2H2z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 4a8 8 0 0 0-8 8h16a8 8 0 0 0-8-8z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="3" r="1.2" fill="currentColor" />
  </svg>
);

const FoodMenu = () => {
  // We showcase popular categories from menuData.js
  const categories = [
    { name: "Fried Chicken", icon: "🍗", dbName: "Fried Chicken" },
    { name: "Burger Delights", icon: "🍔", dbName: "Burger Delights" },
    { name: "Classic Indian Roll", icon: "🌯", dbName: "Classic Indian Roll" },
    { name: "Milk Shakes", icon: "🥤", dbName: "Milk Shakes" },
    { name: "Mojitos", icon: "🍹", dbName: "Mojitos" }
  ];

  const [activeTab, setActiveTab] = useState("Fried Chicken");
  
  // Customization Modal State
  const [selectedItem, setSelectedItem] = useState(null);
  const [addonsSelections, setAddonsSelections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Map database items to high-quality plate assets
  const getItemImage = (item) => {
    const category = item.category.toLowerCase();
    const name = item.name.toLowerCase();
    
    if (category.includes("burger")) return burger;
    if (category.includes("roll")) return roll;
    if (category.includes("fried chicken") || name.includes("drumstick") || name.includes("leg")) return chickenLeg;
    if (category.includes("rice") || name.includes("biryani") || name.includes("noodle")) return friedRice;
    if (category.includes("pizza")) return pizzaPlate;
    if (category.includes("shake") || category.includes("mojito")) return eggSalad;
    
    // Sequential fallback based on item id to create visual variety
    const fallbacks = [p1, p2, p4, p5, chickenLeg, friedRice, pizzaPlate, burger, roll, eggSalad];
    return fallbacks[item.id % fallbacks.length];
  };

  // Open custom modal for item addons
  const openCustomizeModal = (item) => {
    setSelectedItem(item);
    
    // Initialize addons selection structure
    const initialSelections = {};
    item.addons.forEach(group => {
      initialSelections[group.name] = {
        min: group.min,
        max: group.max,
        selectionType: group.selectionType,
        selected: []
      };
    });
    
    setAddonsSelections(initialSelections);
    setModalOpen(true);
  };

  // Handle checking/unchecking addon item
  const handleAddonToggle = (groupName, addonItem) => {
    const groupSelection = addonsSelections[groupName];
    const isSelected = groupSelection.selected.some(i => i.name === addonItem.name);
    
    let updatedSelected = [...groupSelection.selected];
    
    if (groupSelection.selectionType === 'S') {
      // Single selection type (Radio)
      updatedSelected = [addonItem];
    } else {
      // Multiple selection type (Checkbox)
      if (isSelected) {
        updatedSelected = updatedSelected.filter(i => i.name !== addonItem.name);
      } else {
        if (updatedSelected.length < groupSelection.max) {
          updatedSelected.push(addonItem);
        }
      }
    }
    
    setAddonsSelections({
      ...addonsSelections,
      [groupName]: {
        ...groupSelection,
        selected: updatedSelected
      }
    });
  };

  // Calculate customized item total price
  const calculateTotal = () => {
    if (!selectedItem) return 0;
    let total = selectedItem.price;
    Object.keys(addonsSelections).forEach(groupName => {
      addonsSelections[groupName].selected.forEach(addon => {
        total += addon.price;
      });
    });
    return total;
  };

  const handleAddSelection = () => {
    setToastMessage(`Customized "${selectedItem.name}" added to list!`);
    setModalOpen(false);
    setTimeout(() => {
      setToastMessage("");
    }, 3000);
  };

  // Filter items based on active tab and limit to 8 popular preview items
  const activeDbName = categories.find(c => c.name === activeTab)?.dbName || activeTab;
  const filteredItems = MENU_ITEMS.filter(item => item.category === activeDbName).slice(0, 8);

  return (
    <section className="bg-[#faf8f5] py-12 md:py-24 px-4 sm:px-6 relative select-none" id="menu">
      {/* CSS Animation styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes menuFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes modalPopIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-menu-fade {
          animation: menuFadeIn 0.35s ease-out forwards;
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (max-width: 639px) {
          .animate-modal-mobile {
            animation: slideUp 0.32s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        }
        @media (min-width: 640px) {
          .animate-modal-desktop {
            animation: modalPopIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        }
        .animate-toast-slide {
          animation: toastSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}} />

      <div className="max-w-6xl mx-auto bg-white rounded-[24px] sm:rounded-[32px] p-5 sm:p-10 md:p-14 shadow-[0_15px_50px_rgba(7,21,32,0.03)] border border-neutral-100/50">
        
        {/* Header Badge */}
        <div className="flex items-center justify-center gap-2.5 text-[#f25e1c] font-black uppercase text-[10px] sm:text-xs tracking-[0.25em] mb-3">
          <ClocheIcon />
          <span>FOOD MENU</span>
          <ClocheIcon />
        </div>

        {/* Title */}
        <h2 className="text-2xl sm:text-4xl md:text-[42px] font-black text-[#071520] mb-8 md:mb-10 text-center tracking-tight leading-tight">
          Flakes Premium Foods
        </h2>

        {/* Category Tabs Container - Horizontal Swipeable on Mobile */}
        <div className="flex items-center justify-start md:justify-center overflow-x-auto scrollbar-none pb-4 md:pb-6 border-b border-neutral-100 mb-8 md:mb-10 -mx-5 px-5 md:mx-0 md:px-0 scroll-smooth">
          <div className="flex items-center gap-1 sm:gap-2 min-w-max">
            {categories.map((cat, idx) => {
              const isActive = activeTab === cat.name;
              return (
                <React.Fragment key={cat.name}>
                  <button
                    onClick={() => setActiveTab(cat.name)}
                    className={`flex items-center gap-2 px-4.5 py-2.5 transition-all duration-300 group focus:outline-none rounded-full md:rounded-none ${
                      isActive 
                        ? "text-[#cd3a27] bg-[#cd3a27]/5 md:bg-transparent" 
                        : "text-[#071520] hover:text-[#cd3a27]"
                    }`}
                  >
                    {/* Icon Wrapper */}
                    <span className={`text-lg transition-transform duration-300 group-hover:scale-110 ${
                      isActive ? "scale-115" : ""
                    }`}>
                      {cat.icon}
                    </span>
                    
                    {/* Label */}
                    <span className={`text-xs font-extrabold uppercase tracking-wider transition-colors duration-300 ${
                      isActive ? "font-black" : ""
                    }`}>
                      {cat.name}
                    </span>
                  </button>
                  
                  {/* Dotted separator between items, hidden on mobile */}
                  {idx < categories.length - 1 && (
                    <div className="hidden md:block w-px h-6 border-r border-dotted border-neutral-300 mx-2" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Menu Items Grid */}
        <div 
          key={activeTab} 
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 animate-menu-fade"
        >
          {filteredItems.map((item) => {
            const isCustomizable = item.addons && item.addons.length > 0;
            return (
              <div 
                key={item.id} 
                onClick={() => isCustomizable && openCustomizeModal(item)}
                className="flex justify-between items-start p-4 sm:p-5 bg-white rounded-2xl border border-neutral-100 hover:border-orange-500/20 hover:shadow-[0_10px_30px_rgba(205,58,39,0.03)] transition-all duration-300 group cursor-pointer"
              >
                {/* Left Side: Product details */}
                <div className="flex-1 text-left pr-4">
                  
                  {/* Diet & Customization Badges Row */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {/* Veg/Non-Veg FSSAI Indicator Box */}
                    <span className={`w-3.5 h-3.5 flex items-center justify-center border rounded p-[2px] flex-shrink-0 ${
                      item.type === 'veg' ? 'border-emerald-500 text-emerald-500' : 'border-rose-600 text-rose-600'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${item.type === 'veg' ? 'bg-emerald-500' : 'bg-rose-600'}`} />
                    </span>
                    
                    {isCustomizable && (
                      <span className="text-[9px] font-black text-orange-600 uppercase tracking-widest font-mono">
                        Customizable
                      </span>
                    )}
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-sm sm:text-base font-extrabold text-[#071520] transition-colors duration-300 group-hover:text-[#cd3a27] leading-snug">
                    {item.name}
                  </h3>
                  
                  {/* Price */}
                  <span className="text-sm sm:text-base font-black text-[#cd3a27] mt-1 block">
                    ₹{item.price}
                  </span>
                  
                  {/* Description */}
                  <p className="text-[11px] sm:text-xs text-neutral-400 mt-2 select-none font-medium line-clamp-2 leading-relaxed">
                    {item.description || "Freshly cooked to order with signature herbs, spices, and premium ingredients."}
                  </p>
                </div>

                {/* Right Side: Circular Food Image */}
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-neutral-50 rounded-2xl border border-neutral-100/80 shadow-inner overflow-hidden flex items-center justify-center">
                  <img
                    src={getItemImage(item)}
                    alt={item.name}
                    className="w-[85%] h-[85%] object-contain select-none transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
                  />
                  
                  {/* Customizable Add (+) Button badge overlays image bottom right */}
                  {isCustomizable && (
                    <div className="absolute bottom-1 right-1 w-6.5 h-6.5 rounded-lg bg-white shadow-md text-neutral-600 flex items-center justify-center border border-neutral-100 group-hover:bg-[#cd3a27] group-hover:text-white transition-all duration-300">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" className="w-3.5 h-3.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button at the bottom */}
        <div className="flex justify-center mt-10 md:mt-12 z-10 relative">
          <Link to="/menu" className="inline-flex items-center bg-[#cd3a27] hover:bg-[#b02f1e] text-white text-[11px] font-black tracking-widest uppercase pl-6 rounded transition-all duration-300 group shadow-md hover:shadow-lg select-none">
            <span>VIEW FULL MENU ({MENU_ITEMS.length} ITEMS)</span>
            <div className="w-11 h-11 bg-white/10 text-white flex items-center justify-center ml-5 border-l border-white/15">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </Link>
        </div>

      </div>

      {/* Dynamic Success Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-neutral-900 border border-neutral-800 text-white px-5 py-3.5 rounded-xl shadow-2xl flex items-center gap-3 animate-toast-slide">
          <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">
            ✓
          </div>
          <span className="text-xs font-bold font-sans tracking-wide uppercase">
            {toastMessage}
          </span>
        </div>
      )}

      {/* Interactive Customize Modal (Centers on Desktop, Slides from Bottom on Mobile) */}
      {modalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-xs transition-opacity duration-300">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full max-h-[88vh] sm:max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-modal-mobile sm:animate-modal-desktop relative">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-neutral-100 flex justify-between items-start">
              <div>
                <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${selectedItem.type === 'veg' ? 'bg-emerald-500 border border-emerald-300' : 'bg-rose-600 border border-rose-400'}`} />
                <h3 className="font-sans font-black text-base text-[#0f172a] inline-block align-middle">
                  Customize "{selectedItem.name}"
                </h3>
                <p className="text-xs text-neutral-400 mt-1.5 leading-relaxed">
                  {selectedItem.description || "Select your preferred choice of addons below."}
                </p>
              </div>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-neutral-400 hover:text-neutral-950 border border-neutral-200 p-1.5 rounded-full hover:bg-neutral-50 transition-colors focus:outline-none"
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6 scrollbar-none">
              {selectedItem.addons.map((group) => {
                const groupSelection = addonsSelections[group.name] || { selected: [] };
                
                return (
                  <div key={group.name} className="border-b border-neutral-100 last:border-b-0 pb-6 last:pb-0 text-left">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <h4 className="font-sans font-black text-xs text-[#0f172a] uppercase tracking-wider">
                          {group.name}
                        </h4>
                        <span className="text-[9px] text-neutral-400 font-mono font-bold uppercase">
                          {group.min === 0 ? "Optional" : `Required (min ${group.min})`} · max {group.max}
                        </span>
                      </div>
                      <span className="text-[9px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded font-mono uppercase">
                        {group.selectionType === 'S' ? "Select One" : "Select Multiple"}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2">
                      {group.items.map((addon) => {
                        const isChecked = groupSelection.selected.some(i => i.name === addon.name);
                        
                        return (
                          <div 
                            key={addon.name}
                            onClick={() => handleAddonToggle(group.name, addon)}
                            className={`flex justify-between items-center p-3 border rounded-xl cursor-pointer select-none transition-all duration-200 ${
                              isChecked 
                                ? 'border-[#cd3a27] bg-[#faf8f5]' 
                                : 'border-neutral-100 hover:border-neutral-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 flex items-center justify-center border transition-all ${
                                group.selectionType === 'S' ? 'rounded-full' : 'rounded'
                              } ${
                                isChecked ? 'border-[#cd3a27] bg-[#cd3a27] text-white' : 'border-neutral-300 bg-white'
                              }`}>
                                {isChecked && (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-2.5 h-2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </div>

                              <span className="text-xs font-bold text-[#0f172a] font-sans">
                                {addon.name}
                              </span>
                            </div>
                            
                            <span className="text-xs font-black text-[#cd3a27] font-sans">
                              {addon.price > 0 ? `+ ₹${addon.price}` : "FREE"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-neutral-100 bg-[#faf8f5] flex justify-between items-center pb-8 sm:pb-6">
              <div>
                <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider block font-mono">
                  Total price
                </span>
                <span className="font-sans font-black text-xl text-[#cd3a27]">
                  ₹{calculateTotal()}
                </span>
              </div>
              <button 
                onClick={handleAddSelection}
                className="bg-[#cd3a27] hover:bg-[#b02f1e] text-white font-sans text-xs font-black uppercase tracking-widest px-6 py-3 rounded-lg shadow-md active:scale-95 transition-all duration-200"
              >
                Add Selection
              </button>
            </div>

          </div>
        </div>
      )}

    </section>
  );
};

export default FoodMenu;
